import type { Address } from "@jasper/client-models";

import { useTranslation } from "react-i18next";
import { Group, Select, Stack, TextInput, Tooltip } from "@mantine/core";
import * as countryList from "country-codes-list";

import type { FieldValueProps } from "../types";
import { IconInfoCircle } from "@tabler/icons-react";

type Props = FieldValueProps<Address>;

export const AddressForm = ({ value, errors, onChange, onBlur }: Props) => {
  const { t } = useTranslation("forms");

  const getFieldError = (
    error: string | undefined,
    t: (key: string) => string
  ): string | undefined => {
    if (!error) return undefined;
    return error === "invalid_value"
      ? t("common.toProvide")
      : t("common.invalidValue");
  };

  return (
    <Stack w="100%">
      <TextInput
        w="100%"
        label={t("forms.address.labels.addressName")}
        required
        value={value?.addressName}
        onBlur={() => onBlur?.()}
        onChange={e =>
          onChange({ ...value, addressName: e.currentTarget.value })
        }
        error={getFieldError(errors?.addressName, t)}
      ></TextInput>
      <Group w="100%" align="flex-start">
        <TextInput
          flex={1}
          label={
            <>
              {t("forms.address.labels.streetNumber")}
              <Tooltip
                label={t("forms.address.tooltips.streetNumber")}
                withArrow
              >
                <IconInfoCircle
                  style={{
                    width: "1rem",
                    verticalAlign: "bottom",
                    marginLeft: "0.25rem",
                  }}
                />
              </Tooltip>
            </>
          }
          required
          value={value?.streetNumber}
          onChange={e =>
            onChange({ ...value, streetNumber: e.currentTarget.value })
          }
          onBlur={() => onBlur?.()}
          error={getFieldError(errors?.streetNumber, t)}
        />
        <TextInput
          flex={1}
          label={
            <>
              {t("forms.address.labels.streetName")}
              <Tooltip label={t("forms.address.tooltips.streetName")} withArrow>
                <IconInfoCircle
                  style={{
                    width: "1rem",
                    verticalAlign: "bottom",
                    marginLeft: "0.25rem",
                  }}
                />
              </Tooltip>
            </>
          }
          required
          value={value?.streetName}
          onChange={e =>
            onChange({ ...value, streetName: e.currentTarget.value })
          }
          onBlur={() => onBlur?.()}
          error={getFieldError(errors?.streetName, t)}
        />
        <TextInput
          flex={1}
          label={t("forms.address.labels.details")}
          value={value?.details}
          onChange={e => onChange({ ...value, details: e.currentTarget.value })}
          onBlur={() => onBlur?.()}
          error={errors?.details && t("common.invalidValue")}
        />
      </Group>
      <Group w="100%" align="flex-start">
        <TextInput
          flex={1}
          label={t("forms.address.labels.city")}
          required
          value={value?.city}
          onChange={e => onChange({ ...value, city: e.currentTarget.value })}
          onBlur={() => onBlur?.()}
          error={getFieldError(errors?.city, t)}
        />
        <TextInput
          flex={1}
          label={t("forms.address.labels.postalCode")}
          required
          value={value?.postalCode}
          onChange={e =>
            onChange({ ...value, postalCode: e.currentTarget.value })
          }
          onBlur={() => onBlur?.()}
          error={getFieldError(errors?.postalCode, t)}
        />
        <Select
          flex={1}
          label={t("forms.address.labels.country")}
          required
          searchable
          value={value?.country}
          data={countryList.all().map(country => ({
            label: country.countryNameEn,
            value: country.countryNameEn,
          }))}
          onChange={selected =>
            selected && onChange({ ...value, country: selected })
          }
          onBlur={() => onBlur?.()}
          error={getFieldError(errors?.country, t)}
        />
      </Group>
    </Stack>
  );
};
